import { useEffect, useState } from 'react';

import { getTableDescriptor } from '../../api/gameApi';

/**
 * Stands in for "this table has an owner, but we could not find out who".
 *
 * It matters that this is not `null`. The game treats a falsy `ownerId` as "nobody
 * owns this table", and `Model.localPlayerCanInvite()` returns true in that case —
 * so reporting null on a failed lookup would *grant* every player at a private table
 * the invite affordance, which is the opposite of degrading safely. There is no
 * server-side creator check on invites either (v1 has none, deliberately), so
 * nothing downstream would catch it.
 *
 * A negative id can never equal a real numeric player id, so it reads as "owned by
 * someone who is not you": no invite button, no KICK button. The owner loses their
 * own affordances for the session, which is the cheap failure.
 */
const UNKNOWN_TABLE_OWNER = -1;

type UseTableOwnerResult = {
	/**
	 * Numeric player id of the table's owner, `null` when the table genuinely has
	 * none (the public rooms), or UNKNOWN_TABLE_OWNER when the lookup failed.
	 */
	ownerId: number | null;
	/**
	 * True once the lookup has settled, successfully or not. Hosts wait on this
	 * before starting the game but must never block on it indefinitely — a table
	 * with an unknown owner is still playable, it just has no owner powers.
	 */
	resolved: boolean;
};

/**
 * Resolves the owner of a Svara table.
 *
 * The shared Pixi game reads `initData.ownerId` once at startGame and uses it for
 * the two owner-only affordances: inviting someone into an empty seat
 * (Model.localPlayerCanInvite) and the KICK button in the player menu
 * (Model.isLocalPlayerOwner). It has to be present *before* the game starts, so
 * it cannot come from the getTable socket payload, which arrives afterwards.
 *
 * Resolved here by table id rather than threaded through navigation params: there
 * are a dozen routes into the game screen (lobby tap, quick seat, my tables, VIP
 * tables, invite accept, tournaments, table switch) and several of them never had
 * the table object to begin with. One lookup keyed on the id that every one of
 * them already passes keeps the permission from depending on which door was used.
 */
export function useTableOwner(
	tableId: string | number | undefined,
	options: { enabled: boolean },
): UseTableOwnerResult {
	const { enabled } = options;
	const [ownerId, setOwnerId] = useState<number | null>(null);
	const [resolved, setResolved] = useState(false);

	useEffect(() => {
		if (!enabled || tableId == null) {
			// Nothing to look up (tutorial, tournament, or no table yet): report
			// "no owner", settled, so the host does not wait on us.
			setOwnerId(null);
			setResolved(true);
			return;
		}

		let cancelled = false;
		setResolved(false);

		getTableDescriptor(tableId)
			.then(descriptor => {
				if (cancelled) return;
				// `null` here is a real answer — the public rooms have no creator, and v1
				// let anyone invite at those — so it is passed through as-is.
				setOwnerId(descriptor.owner);
			})
			.catch(error => {
				if (cancelled) return;
				// Owner unknown. Report UNKNOWN_TABLE_OWNER rather than null so the
				// table stays playable without handing out owner-only affordances —
				// see the constant. Never block on this: a table that will not open is
				// worse than one whose owner has no kick button this session.
				console.log('useTableOwner: failed to resolve table owner', String(error));
				setOwnerId(UNKNOWN_TABLE_OWNER);
			})
			.finally(() => {
				if (!cancelled) setResolved(true);
			});

		return () => {
			cancelled = true;
		};
	}, [enabled, tableId]);

	return { ownerId, resolved };
}
